/**
 * Copyright (c) 2022, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
 * You may not alter or remove any copyright or other notice from copies of this content.
 */

import axios, { AxiosResponse } from "axios";
import React, { PropsWithChildren, ReactElement, useEffect, useState } from "react";
import { DEPLOYMENT_CONFIG_FILE_PATH } from "../constants";
import { ConfigContext } from "../contexts";
import { DeploymentConfigInterface } from "../models";

// eslint-disable-next-line @typescript-eslint/ban-types
type ConfigProviderPropsInterface = {};

/**
 * Configuration Provider.
 *
 * @param {React.PropsWithChildren<ConfigProviderPropsInterface>} props - Props injected to the component.
 * @returns {React.ReactElement}
 */
const ConfigProvider = (props: PropsWithChildren<ConfigProviderPropsInterface>): ReactElement => {

    const { children } = props;

    const [ deploymentConfig, setDeploymentConfig ] = useState<DeploymentConfigInterface | undefined>(undefined);

    useEffect(() => {
        axios.get(DEPLOYMENT_CONFIG_FILE_PATH)
            .then((response: AxiosResponse<DeploymentConfigInterface>) => {
                // TODO: Show a UI error message and break the application.
                if (!response?.data || response.status !== 200) {
                    throw new Error("An error occured while getting the Deployment config.");
                }

                if (response.data.ui?.appCopyright) {
                    const year = new Date().getFullYear();

                    response.data.ui.appCopyright = response.data.ui.appCopyright
                        .replace("${copyright}", "\u00A9")
                        .replace("${year}", year.toString());
                }

                // TODO: Remove these once we have a proper guide to handle Asgardeo local setup.
                // https://github.com/wso2-enterprise/asgardeo-product/issues/11089#issuecomment-1162686697
                if (response.data?.serverOrigin === "{SERVER_ORIGIN}") {
                    response.data.serverOrigin = "https://api.asg.io";
                }

                if (response.data?.links?.console === "{CONSOLE_HOST}") {
                    response.data.links.console = "https://console.asg.io";
                }

                setDeploymentConfig(response.data);
            })
            .catch(() => {
                // TODO: Show a UI error message and break the application.
            });
    }, []);

    return (
        <ConfigContext.Provider
            value={ {
                deployment: deploymentConfig
            } }>
            { children }
        </ConfigContext.Provider>
    );
};

export default ConfigProvider;
